import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useAdminLang } from "@/auth/AdminLangCtx";
import { LogOut, ExternalLink, Menu, X } from "lucide-react";

const GRAD = "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)";
const C = {
  blue: "#3B82F6",
  purple: "#8B5CF6",
  bg: "#f4f6ff",
  card: "#ffffff",
  text: "#0d0d1a",
  muted: "#4a4a6a",
  border: "#e2e8f8",
};

export default function DashShell({ title, subtitle, nav, children, role }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { lang, setLang, t } = useAdminLang();

  const NavLinks = ({ onClick }) =>
    nav.map((item) => {
      const active = loc.pathname === item.to || (item.match && loc.pathname.startsWith(item.match));
      return (
        <Link
          key={item.to}
          to={item.to}
          onClick={onClick}
          data-testid={`nav-${item.key}`}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all"
          style={
            active
              ? { background: GRAD, color: "#fff", boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }
              : { color: C.muted, background: "transparent" }
          }
          onMouseEnter={(e) => {
            if (!active) {
              e.currentTarget.style.background = "rgba(59,130,246,.08)";
              e.currentTarget.style.color = C.blue;
            }
          }}
          onMouseLeave={(e) => {
            if (!active) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = C.muted;
            }
          }}
        >
          <item.icon className="w-4 h-4 shrink-0" />
          <span className="truncate">{item.label}</span>
          {typeof item.badge === "number" && item.badge > 0 && (
            <span
              className="ml-auto shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={active ? { background: "#fff", color: C.blue } : { background: GRAD, color: "#fff" }}
            >
              {item.badge}
            </span>
          )}
        </Link>
      );
    });

  return (
    <div className="min-h-screen" data-testid={`dash-shell-${role}`}
      style={{ background: C.bg, color: C.text, fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-40"
        style={{ background: "rgba(244,246,255,.85)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Hamburger — mobile only */}
            <button
              className="lg:hidden shrink-0 p-2 rounded-lg transition-colors"
              style={{ color: C.muted }}
              onClick={() => setMobileNavOpen(true)}
              aria-label={t("shell.openMenu")}
            >
              <Menu size={20} />
            </button>
            <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="dash-logo">
              <div className="w-8 h-8 shrink-0 rounded-xl flex items-center justify-center text-white font-extrabold text-sm"
                style={{ background: GRAD, boxShadow: "0 4px 12px -4px rgba(59,130,246,.6)" }}>N</div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.1rem", letterSpacing: "-0.02em", color: C.text }} className="truncate">
                Neobk<span style={{ background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
              </span>
              <span className="hidden sm:inline-flex ml-1 text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-full shrink-0"
                style={{ background: "rgba(59,130,246,.1)", color: C.blue, border: "1px solid rgba(59,130,246,.2)" }}>
                {role === "super_admin" ? t("shell.superAdmin") : t("shell.admin")}
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* FR / EN language toggle */}
            <div className="hidden sm:flex items-center rounded-full overflow-hidden text-xs font-bold border" style={{ borderColor: C.border }}>
              <button
                onClick={() => setLang("fr")}
                className="px-3 py-1.5 transition-colors"
                style={lang === "fr" ? { background: GRAD, color: "#fff" } : { color: C.muted }}
              >FR</button>
              <button
                onClick={() => setLang("en")}
                className="px-3 py-1.5 transition-colors"
                style={lang === "en" ? { background: GRAD, color: "#fff" } : { color: C.muted }}
              >EN</button>
            </div>
            <div className="text-right hidden md:block px-2">
              <div className="text-sm font-semibold" style={{ color: C.text }}>{user?.name}</div>
              <div className="text-xs" style={{ color: C.muted }}>{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="dash-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5"
              style={{ background: "rgba(239,68,68,.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,.2)" }}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t("shell.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col shadow-2xl" style={{ background: C.card }}>
            <div className="flex items-center justify-between px-5 h-16 border-b shrink-0" style={{ borderColor: C.border }}>
              <div className="flex items-center gap-3">
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, color: C.text }}>{t("shell.navigation")}</span>
                <div className="flex items-center border rounded-full overflow-hidden text-xs font-bold" style={{ borderColor: C.border }}>
                  <button onClick={() => setLang("fr")} className="px-2.5 py-1 transition-colors"
                    style={lang === "fr" ? { background: GRAD, color: "#fff" } : { color: C.muted }}>FR</button>
                  <button onClick={() => setLang("en")} className="px-2.5 py-1 transition-colors"
                    style={lang === "en" ? { background: GRAD, color: "#fff" } : { color: C.muted }}>EN</button>
                </div>
              </div>
              <button onClick={() => setMobileNavOpen(false)} className="p-2 rounded-lg" style={{ color: C.muted }}
                aria-label={t("shell.closeMenu")}>
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              <NavLinks onClick={() => setMobileNavOpen(false)} />
            </nav>
            <div className="p-4 border-t shrink-0" style={{ borderColor: C.border }}>
              <div className="text-sm font-semibold" style={{ color: C.text }}>{user?.name}</div>
              <div className="text-xs mt-0.5" style={{ color: C.muted }}>{user?.email}</div>
            </div>
          </aside>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-8 grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block lg:sticky lg:top-24 self-start">
          <nav className="space-y-1">
            <NavLinks />
          </nav>
        </aside>

        {/* Main content */}
        <main className="min-w-0">
          {title && (
            <div className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 mb-2">
                  <span className="w-6 h-px" style={{ background: GRAD }} />
                  <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ color: C.blue }}>
                    {role === "super_admin" ? t("shell.superAdminSpace") : t("shell.adminSpace")}
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.6rem, 3vw, 2.2rem)", letterSpacing: "-0.02em", color: C.text }}>{title}</h1>
                {subtitle && <p className="mt-1.5 text-sm sm:text-base" style={{ color: C.muted }}>{subtitle}</p>}
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

export function StatCard({ label, value, sub, color = "default", testId }) {
  return (
    <div data-testid={testId}
      className="rounded-2xl p-5 transition-all hover:-translate-y-1"
      style={{ background: C.card, border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(59,130,246,.05)" }}>
      <div className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ color: C.muted }}>{label}</div>
      <div className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-2"
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          ...(color === "gold"
            ? { background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
            : { color: C.text }),
        }}>
        {value}
      </div>
      {sub && <div className="text-xs mt-2" style={{ color: C.muted }}>{sub}</div>}
    </div>
  );
}

export function ExternalIcon() { return <ExternalLink className="w-3 h-3 inline" />; }
